import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import {
	Box,
	Button,
	Dialog,
	Grid,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../hooks/useSettings";
import { useEditCargoQuickOptionsMutation } from "../cargoApiSlice";

import { useCargoQuickOptions } from "../../../hooks/useCargoQuickOptions";

const getItems = (count, defaultValues) =>
	Array.from({ length: count }, (v, k) => k).map((k) => ({
		id: `item-${k}`,
		value: defaultValues[k],
	}));

const reorder = (list, startIndex, endIndex) => {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);

	return result;
};

const grid = 8;

const getItemStyle = (draggableStyle, isDragging) => ({
	// some basic styles to make the items look a bit nicer
	userSelect: "none",
	padding: grid * 2,
	margin: `0 0 ${grid}px 0`,
	border: "1px solid #ccc",
	borderRadius: "5px",
	// change background colour if dragging
	background: isDragging ? "#efefef" : "#fff",

	// styles we need to apply on draggables
	...draggableStyle,
});

const generateInputConfigurationByType = (type, settings, defaultValues) => {
	switch (type) {
		case "weight":
			return {
				input: {
					component: "input",
					count: 3,
				},
				defaultValues: defaultValues[type],
			};
		case "length":
			return {
				input: {
					component: "input",
					count: 3,
				},
				defaultValues: defaultValues[type],
			};
		case "vehicleTypeId":
			return {
				input: {
					component: "select",
					options: Object.entries(settings.vehicleOptions.vehicleTypesOptions)
						.filter(([value, _label]) => {
							return value !== "ANY_TYPE";
						})
						.map(([value, label]) => {
							return { value, label: label.name };
						}),
					count: 3,
				},
				defaultValues: defaultValues[type],
			};
		case "vehicleBodyId":
			return {
				input: {
					component: "select",
					options: Object.entries(
						settings.vehicleOptions.vehicleBodyTypesOptions
					).map(([value, label]) => {
						return { value, label };
					}),
					count: 2,
				},
				defaultValues: defaultValues[type],
			};
		case "additionalRequirements":
			return {
				input: {
					component: "select",
					options: Object.entries(
						settings.vehicleOptions.additionalRequirementsOptions
					).map(([value, label]) => {
						return { value, label };
					}),
					count: 5,
				},
				defaultValues: defaultValues[type],
			};
	}
};

export const ParametersEdit = ({ type }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { settings } = useSettings();
	const {
		weightOptions,
		lengthOptions,
		vehicleTypeOptions,
		vehicleBodyOptions,
		additionalRequirementsOptions,
	} = useCargoQuickOptions();

	const [updateQuickOptionsMutation, { isLoading: isUpdating }] =
		useEditCargoQuickOptionsMutation();

	const [open, setOpen] = useState(false);
	const [itemsErrors, setItemsErrors] = useState([]);

	const [defaultValues, setDefaultValues] = useState({
		weight: weightOptions,
		length: lengthOptions,
		vehicleTypeId: vehicleTypeOptions,
		vehicleBodyId: vehicleBodyOptions,
		additionalRequirements: additionalRequirementsOptions,
	});
	const [configuration, setConfiguration] = useState(
		generateInputConfigurationByType(type, settings, defaultValues)
	);
	const [items, setItems] = useState(
		getItems(configuration.input.count, configuration.defaultValues)
	);

	useEffect(() => {
		// Generate new default values whenever options change
		const defaults = {
			weight: weightOptions,
			length: lengthOptions,
			vehicleTypeId: vehicleTypeOptions,
			vehicleBodyId: vehicleBodyOptions,
			additionalRequirements: additionalRequirementsOptions,
		};
		setDefaultValues(defaults);

		// Generate new configuration based on type and settings
		const newConfiguration = generateInputConfigurationByType(
			type,
			settings,
			defaults
		);
		setConfiguration(newConfiguration);

		// Generate new items based on the new configuration
		setItems(
			getItems(newConfiguration.input.count, newConfiguration.defaultValues)
		);
	}, [
		weightOptions,
		lengthOptions,
		vehicleTypeOptions,
		vehicleBodyOptions,
		additionalRequirementsOptions,
		type,
		settings,
	]);

	const onDragEnd = (result) => {
		// dropped outside the list
		if (!result.destination) {
			return;
		}

		const newItems = reorder(
			items,
			result.source.index,
			result.destination.index
		);

		setItems(newItems);
	};

	const handleSave = async () => {
		// Reset errors
		setItemsErrors([]);

		const errors = {
			empty: [],
			duplicates: [],
		};

		const valueMap = new Map();

		items.forEach((item, index) => {
			// Check for empty values
			if (item.value === "") {
				errors.empty.push(index);
			}

			// Track duplicates
			if (valueMap.has(item.value)) {
				errors.duplicates.push(index);
				// Add the first occurrence of the duplicate as well
				if (!errors.duplicates.includes(valueMap.get(item.value))) {
					errors.duplicates.push(valueMap.get(item.value));
				}
			} else {
				valueMap.set(item.value, index);
			}
		});

		// If there are empty values, show an error
		if (errors.empty.length > 0) {
			enqueueSnackbar(t("values_cant_be_empty"), {
				variant: "error",
			});
			setItemsErrors(errors.empty);
			return;
		}

		// If there are duplicates, show an error
		if (errors.duplicates.length > 0) {
			enqueueSnackbar(t("values_has_to_be_unique"), {
				variant: "error",
			});
			setItemsErrors(errors.duplicates);
			return;
		}

		// You can send the new order to the backend here
		const data = Object.entries(items).map(([_, item]) =>
			type === "weight" || type === "length" ? +item.value : item.value
		);

		await updateQuickOptionsMutation({ type, data })
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_quick_options"), {
					variant: "success",
				});
				setOpen(false);
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	const handleInputChange = (index, newValue) => {
		setItems((prevItems) => {
			const updatedItems = [...prevItems];
			updatedItems[index].value = newValue;
			return updatedItems;
		});

		const duplicates = items.reduce((acc, item, index) => {
			const isDuplicate = items.some(
				(i, iIndex) => i.value === item.value && iIndex !== index
			);
			if (isDuplicate) {
				acc.push(index);
			}
			return acc;
		}, []);

		setItemsErrors(duplicates);
	};

	return (
		<>
			<Tooltip
				title={
					<Typography variant="p" sx={{ fontSize: "9pt" }} component="span">
						{t("double_click_to_edit_quick_options")}
					</Typography>
				}
				placement="top"
			>
				<Box
					sx={{
						position: "absolute",
						top: 0,
						right: 0,
						width: 0,
						height: 0,
						borderBottom: "20px solid transparent",
						borderRight: "20px solid #55b7e6",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
					}}
					onDoubleClick={() => setOpen(true)}
				>
					<EditIcon
						sx={{
							position: "absolute",
							top: "0",
							right: "-20px",
							color: "#fff",
							fontSize: 12,
							transform: "rotate(90deg)",
						}}
					/>
				</Box>
			</Tooltip>
			<Dialog
				fullWidth
				maxWidth="xs"
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box sx={{ padding: "30px" }}>
					<Grid container rowSpacing={3} columnSpacing={2}>
						<Grid size={12}>
							<Box sx={{ textAlign: "center" }}>
								<Typography
									component="h3"
									variant="h5"
									sx={{ marginTop: 0, fontWeight: 600 }}
								>
									{t("edit_quick_options")}
								</Typography>
							</Box>
						</Grid>
						<Grid size={12}>
							<DragDropContext onDragEnd={onDragEnd}>
								<Droppable droppableId="droppable">
									{(provided, _snapshot) => (
										<div ref={provided.innerRef} {...provided.droppableProps}>
											{items.map((item, index) => (
												<Draggable
													key={item.id}
													draggableId={item.id}
													index={index}
												>
													{(provided, snapshot) => (
														<div>
															<div
																ref={provided.innerRef}
																{...provided.dragHandleProps}
																{...provided.draggableProps}
																style={getItemStyle(
																	provided.draggableProps.style,
																	snapshot.isDragging
																)}
															>
																<Box
																	sx={{
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		gap: "10px",
																	}}
																>
																	{configuration.input.component === "input" ? (
																		<TextField
																			sx={{ width: "100%" }}
																			size="small"
																			type="text"
																			value={item.value}
																			error={itemsErrors.some(
																				(i) => i === index
																			)}
																			onChange={(e) => {
																				let newValue = e.target.value;

																				// Allow only valid numbers with up to 2 decimal places, and only one comma or decimal point
																				if (
																					newValue === "" ||
																					/^[\d]*[.,]?[\d]{0,2}$/.test(newValue)
																				) {
																					// Normalize the value to always use a period for calculations
																					newValue = newValue.replace(",", ".");

																					// Update the state
																					handleInputChange(index, newValue);
																				}
																			}}
																			slotProps={{
																				htmlInput: {
																					inputMode: "decimal",
																					step: ".01",
																					lang: "en",
																					min: 0.01,
																				},
																			}}
																		/>
																	) : (
																		<Select
																			sx={{ width: "100%" }}
																			value={item.value}
																			size="small"
																			error={itemsErrors.some(
																				(i) => i === index
																			)}
																			onChange={(e) =>
																				handleInputChange(index, e.target.value)
																			}
																		>
																			{configuration.input.options.map(
																				(option) => (
																					<MenuItem
																						disabled={items.some(
																							(i) => i.value === option.value
																						)}
																						key={option.value}
																						value={option.value}
																					>
																						{option.label}
																					</MenuItem>
																				)
																			)}
																		</Select>
																	)}

																	<DragIndicatorIcon />
																</Box>
															</div>
															{provided.placeholder}
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>
						</Grid>
						<Grid size={6}>
							<Box sx={{ display: "flex", justifyContent: "end" }}>
								<Button
									type="submit"
									loading={isUpdating}
									variant="contained"
									onClick={handleSave}
								>
									{t("save")}
								</Button>
							</Box>
						</Grid>
						<Grid size={6}>
							<Box sx={{ display: "flex", justifyContent: "start" }}>
								<Button
									variant="contained"
									color="secondary"
									onClick={() => setOpen(false)}
								>
									{t("cancel")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Dialog>
		</>
	);
};
